import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  KeyRound, ShieldCheck, X as XIcon, Check, ExternalLink, Compass,
  RotateCcw, Database, Globe, ChevronRight, ChevronDown,
} from "lucide-react";

import {
  fetchAuthProfiles, startSession, pollSessionStatus,
  signalLoggedIn, signalSessionDone, signalSessionCancel,
  getSessionCapture, setCredentials, validateSession, patchChannel,
  type AuthProfileRow, type SessionStatus, type SessionMode,
} from "../api/crawlerOps";
import { explainError } from "../api/client";
import { shortDate } from "../lib/format";
import { useVncPass } from "../lib/vncPass";

/* ──────────────────────────────────────────────────────────────────────
   /crawler-ops/sessions — channel-scoped publisher-login admin.

   One unified capture mechanism (scripts/playwright_session.py), two modes:
     · Login / Re-login  — fresh login, NEW cookie jar (replaces)
     · Navigate          — augment the existing session (backup tool)

   Both run the same exhaustive capture: cookies + localStorage +
   sessionStorage + IndexedDB + full network trace + per-domain auth map.
   ────────────────────────────────────────────────────────────────────── */

interface OpenSession {
  channelId: number;
  mode: SessionMode;
}

export function CrawlerSessions() {
  const qc = useQueryClient();
  const profiles = useQuery({
    queryKey: ["crawler/auth"],
    queryFn:  fetchAuthProfiles,
    refetchInterval: 30_000,
  });

  const [open, setOpen] = useState<OpenSession | null>(null);
  const openRow = useMemo(
    () => profiles.data?.rows.find(r => r.channel_id === open?.channelId) ?? null,
    [profiles.data, open],
  );

  return (
    <div className="p-6 max-w-[1080px] mx-auto">
      <header className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
          Crawler · Sessions
        </div>
        <h1 className="font-display text-[28px] leading-tight">Publisher logins</h1>
        <p className="font-sans text-[13px] text-[var(--fg-muted)] mt-1 max-w-[680px]">
          One row per newspaper. <strong>Login</strong> opens the publisher's login
          page on np92 in a remote browser — log in, then keep clicking to capture
          every endpoint. <strong>Navigate</strong> reopens the saved session to
          pick up anything that was missed, without re-logging-in.
        </p>
      </header>

      {profiles.isLoading && (
        <div className="font-mono text-[11px] text-[var(--fg-faint)]">loading…</div>
      )}
      {profiles.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {explainError(profiles.error)}
        </div>
      )}

      {profiles.data && (
        <div className="np-card overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                <Th>Newspaper</Th>
                <Th>Login URL</Th>
                <Th>Status</Th>
                <Th>Auth expires</Th>
                <Th>Lifts</Th>
                <Th>Last login</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {profiles.data.rows.map(r => (
                <Row
                  key={r.channel_id}
                  row={r}
                  onSession={(mode) => setOpen({ channelId: r.channel_id, mode })}
                  onValidated={() => qc.invalidateQueries({ queryKey: ["crawler/auth"] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && openRow && (
        <SessionModal
          row={openRow}
          mode={open.mode}
          onClose={() => setOpen(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ["crawler/auth"] });
          }}
        />
      )}
    </div>
  );
}


/* ────────────────── Row ─────────────────────────────────────────── */
function Row({
  row, onSession, onValidated,
}: {
  row: AuthProfileRow;
  onSession: (mode: SessionMode) => void;
  onValidated: () => void;
}) {
  const validate = useMutation({
    mutationFn: () => validateSession(row.channel_id),
    onSuccess: () => onValidated(),
  });

  const [showCookies, setShowCookies] = useState(false);

  const hasSession = row.has_storage_state || row.has_cookies;

  // Expiry status pill colour
  const expiry = row.cookies_expire_at ? new Date(row.cookies_expire_at) : null;
  const now = Date.now();
  // A recent successful validation (stamped by the crawl runner on every
  // authed fetch and by the 95-min session refresher) is ground truth that
  // the stored session works. CookiesExpireAt is only a heuristic — the
  // min TTL among auth-flavoured cookies — and reads short or stale
  // whenever a publisher mints 1-day tokens (TOI's eToken), so it must
  // never paint a working session as expired.
  const validatedRecently =
    row.last_login_status === "ok" &&
    !!row.last_validated_at &&
    now - new Date(row.last_validated_at).getTime() < 36 * 3600_000;
  let pillColor: string;
  let pillText: string;
  if (row.last_login_status === "failing") {
    pillColor = "var(--accent)"; pillText = "failing";
  } else if (!hasSession) {
    pillColor = "var(--fg-faint)"; pillText = "no session";
  } else if (validatedRecently) {
    pillColor = "var(--primary)"; pillText = "ok";
  } else if (!expiry) {
    pillColor = "var(--fg-faint)"; pillText = "no expiry data";
  } else if (expiry.getTime() < now) {
    pillColor = "var(--accent)"; pillText = "expired";
  } else if (expiry.getTime() < now + 24 * 3600_000) {
    pillColor = "#c47c2e"; pillText = "< 24 h";
  } else {
    pillColor = "var(--primary)"; pillText = "ok";
  }

  const daysToExpiry = expiry
    ? Math.round((expiry.getTime() - now) / 3600_000 / 24)
    : null;

  const cs = row.cookie_summary ?? null;
  const hasBreakdown = !!cs && (cs.soonest_auth_cookies.length > 0 || cs.auth_session_cookies.length > 0);

  return (
    <>
    <tr style={{ borderTop: "1px solid var(--rule)" }}>
      <Td>
        <div className="font-display text-[14px]">{row.channel_name}</div>
        <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
          {row.label} · ch{row.channel_id} · {row.auth_type}
          {row.has_credentials && (
            <span className="ml-1.5 text-[var(--primary)]">· creds on file</span>
          )}
        </div>
      </Td>
      <Td>
        {row.login_url ? (
          <a href={row.login_url} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] text-[var(--primary)] hover:underline inline-flex items-center gap-1">
            {new URL(row.login_url).host}
            <ExternalLink size={9} />
          </a>
        ) : (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">—</span>
        )}
      </Td>
      <Td>
        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-folio"
              style={{ background: "var(--bg-inset)", color: pillColor }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: pillColor }} />
          {pillText}
        </span>
      </Td>
      <Td>
        {expiry ? (
          <button
            type="button"
            onClick={() => hasBreakdown && setShowCookies(v => !v)}
            disabled={!hasBreakdown}
            className="font-mono text-[11px] text-left inline-flex items-center gap-1 disabled:cursor-default"
            title={hasBreakdown ? "Show per-cookie breakdown" : ""}
          >
            {hasBreakdown && (
              showCookies
                ? <ChevronDown size={9} className="text-[var(--fg-faint)]" />
                : <ChevronRight size={9} className="text-[var(--fg-faint)]" />
            )}
            <span>{shortDate(expiry.toISOString())}</span>
            <span className="text-[var(--fg-faint)]">
              ({daysToExpiry !== null && daysToExpiry >= 0 ? `+${daysToExpiry}d` : `${daysToExpiry}d`})
            </span>
          </button>
        ) : (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">—</span>
        )}
      </Td>
      <Td>
        {row.lifts.length === 0 ? (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">—</span>
        ) : (
          <div className="font-mono text-[10px] leading-snug max-w-[200px]">
            <span className="text-[var(--primary)]">{row.lifts.length}</span>
            <span className="text-[var(--fg-faint)] ml-1">
              {row.lifts.length === 1 ? "channel" : "channels"}
            </span>
            <div className="text-[var(--fg-muted)] mt-0.5">
              {row.lifts.map(l => l.channel_name).join(" · ")}
            </div>
          </div>
        )}
      </Td>
      <Td>
        {row.last_login_at ? (
          <span className="font-mono text-[11px] text-[var(--fg-muted)]">
            {shortDate(row.last_login_at)}
          </span>
        ) : (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">never</span>
        )}
      </Td>
      <Td className="pr-4">
        {/* flex-wrap lets the buttons stack on narrow windows instead of
            overflowing the card and getting clipped invisible */}
        <div className="flex flex-wrap justify-end gap-1">
          {hasSession && (
            <button
              onClick={() => validate.mutate()}
              disabled={validate.isPending}
              className="np-btn-ghost text-[11px] whitespace-nowrap"
              title="Test the stored session against a real fetch"
            >
              {validate.isPending ? "…" : <><ShieldCheck size={10} className="inline mr-1" />Test</>}
            </button>
          )}
          {hasSession && (
            <button
              onClick={() => onSession("navigate")}
              className="np-btn-ghost text-[11px] whitespace-nowrap"
              title="Reopen the saved session and capture anything that was missed"
            >
              <Compass size={10} className="inline mr-1" />Navigate
            </button>
          )}
          <button
            onClick={() => onSession("login")}
            className="np-btn text-[11px] whitespace-nowrap"
            title={hasSession
              ? "Fresh login — replaces the whole stored cookie jar"
              : "Log in to the publisher and capture the session"}
          >
            {hasSession
              ? <><RotateCcw size={10} className="inline mr-1" />Re-login</>
              : <><KeyRound size={10} className="inline mr-1" />Login</>}
          </button>
        </div>
      </Td>
    </tr>

    {showCookies && cs && (
      <tr>
        <td colSpan={7} style={{ background: "var(--bg-inset)", borderTop: "1px solid var(--rule)" }}>
          <div className="px-4 py-3">
            <div className="flex items-baseline gap-4 mb-2 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
              <span>Cookie jar</span>
              <span className="text-[var(--fg-muted)] normal-case tracking-normal">
                {cs.total} total · {cs.valid_future} valid · {cs.session_cookies} session-only · {cs.expired} expired
              </span>
            </div>

            {cs.soonest_auth_cookies.length > 0 && (
              <div className="mb-3">
                <div className="font-mono text-[10px] text-[var(--fg-muted)] mb-1">
                  Soonest-expiring auth cookies (the ones that actually determine session validity):
                </div>
                <table className="w-full text-[11px] font-mono">
                  <thead>
                    <tr className="text-[var(--fg-faint)]">
                      <th className="text-left pr-3 pb-0.5">name</th>
                      <th className="text-left pr-3 pb-0.5">domain</th>
                      <th className="text-left pr-3 pb-0.5">expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cs.soonest_auth_cookies.map((c, i) => {
                      const exp = c.expires_iso ? new Date(c.expires_iso) : null;
                      const tooSoon = exp && exp.getTime() < Date.now() + 24 * 3600_000;
                      return (
                        <tr key={i}>
                          <td className="pr-3 py-0.5">{c.name}</td>
                          <td className="pr-3 py-0.5 text-[var(--fg-muted)]">{c.domain}</td>
                          <td className="pr-3 py-0.5" style={{ color: tooSoon ? "#c47c2e" : "var(--fg)" }}>
                            {exp ? exp.toISOString().replace("T", " ").slice(0, 19) + "Z" : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {cs.auth_session_cookies.length > 0 && (
              <div>
                <div className="font-mono text-[10px] text-[var(--fg-muted)] mb-1">
                  Auth-flavoured <strong>session</strong> cookies (no expiry — die with the browser; server can also kill them server-side):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cs.auth_session_cookies.map((c, i) => (
                    <span key={i} className="font-mono text-[11px] px-1.5 py-0.5"
                          style={{ background: "var(--bg)", border: "1px solid var(--rule)", color: "#c47c2e" }}
                          title={c.domain}>
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {cs.soonest_auth_cookies.length === 0 && cs.auth_session_cookies.length === 0 && (
              <div className="font-mono text-[10px] text-[var(--fg-faint)]">
                No auth-flavoured cookies in the jar. Either the publisher uses parent-domain SSO
                (so cookies live elsewhere) or the row needs a fresh Login.
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}


/* ────────────────── Unified session modal ─────────────────────────── */
function SessionModal({
  row, mode, onClose, onDone,
}: {
  row: AuthProfileRow;
  mode: SessionMode;
  onClose: () => void;
  onDone: () => void;
}) {
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [capture, setCapture] = useState<unknown>(null);

  // "Logged in to the e-paper?" — operator-recorded fact that this capture
  // involved a REAL login (vs an anonymous session capture). Writes
  // Channel_Crawler.LoggedInCapture; the runner never reads it.
  const [didLogin, setDidLogin] = useState<boolean>(!!row.logged_in_capture);
  const markLoggedIn = useMutation({
    mutationFn: (v: boolean) => patchChannel(row.channel_id, { logged_in_capture: v }),
    onSuccess: () => onDone(),
  });

  const start = useMutation({
    mutationFn: () => startSession(row.channel_id, mode),
    onSuccess: (resp) => setStatus(resp.status),
    onError: (e) => setErr(explainError(e)),
  });
  useEffect(() => { start.mutate(); /* eslint-disable-line */ }, []);

  // Poll while the session is mid-flight.
  useEffect(() => {
    if (!status) return;
    if (["done", "failed", "cancelled", "timeout"].includes(status.status)) return;
    const t = setInterval(async () => {
      try { setStatus(await pollSessionStatus(row.channel_id)); }
      catch (e) { setErr(explainError(e)); }
    }, 2000);
    return () => clearInterval(t);
  }, [row.channel_id, status?.status]);

  const loggedIn = useMutation({
    mutationFn: () => signalLoggedIn(row.channel_id),
    onSuccess: (s) => setStatus(s),
    onError: (e) => setErr(explainError(e)),
  });
  const done = useMutation({
    mutationFn: () => signalSessionDone(row.channel_id),
    onSuccess: async (s) => {
      setStatus(s);
      if (s.status === "done") {
        try { setCapture(await getSessionCapture(row.channel_id)); }
        catch (e) { setErr(explainError(e)); }
        onDone();
      }
    },
    onError: (e) => setErr(explainError(e)),
  });
  const cancel = useMutation({
    mutationFn: () => signalSessionCancel(row.channel_id),
    onSuccess: (s) => { setStatus(s); setTimeout(onClose, 400); },
  });

  const phase = status?.status ?? "starting";
  const terminal = ["done", "failed", "cancelled", "timeout"].includes(phase);
  const wantIframe = !!status?.novnc_url &&
    (phase === "waiting_for_login" || phase === "waiting_for_navigate");
  const vncPass = useVncPass(wantIframe);
  const showIframe = wantIframe && vncPass.ready;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4"
         role="dialog" aria-modal="true"
         style={{ background: "rgba(8,10,15,0.74)" }}>
      <div onClick={() => terminal && onClose()} className="absolute inset-0" />
      {/* Near-fullscreen: the embedded noVNC browser needs every pixel it
          can get for the operator to read the publisher's page. */}
      <div className="relative w-[96vw] max-w-[1880px] flex flex-col"
           style={{
             background: "var(--bg-card)",
             border: "1px solid var(--rule-strong)",
             boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
             height: "94vh",
           }}>
        {/* header */}
        <div className="px-5 py-3 flex items-center gap-3"
             style={{ borderBottom: "1px solid var(--rule)" }}>
          {mode === "login"
            ? <KeyRound size={14} className="text-[var(--primary)]" />
            : <Compass size={14} className="text-[var(--primary)]" />}
          <h3 className="font-display text-[18px] leading-none">
            {mode === "login"
              ? (row.has_storage_state ? "Re-login" : "Login")
              : "Navigate"} · {row.channel_name}
          </h3>
          <PhasePill phase={phase} />
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">
            {mode === "login" ? "new cookie jar" : "augments saved session"}
          </span>
          <button onClick={onClose} aria-label="Close" className="ml-auto np-btn-ghost p-1.5">
            <XIcon size={12} />
          </button>
        </div>

        {/* status line */}
        <div className="px-5 py-2 font-mono text-[11px] text-[var(--fg-muted)] leading-snug flex items-center gap-3"
             style={{ borderBottom: "1px solid var(--rule)", background: "var(--bg-inset)" }}>
          <span className="flex-1">{status?.message ?? "starting up…"}</span>
          {typeof status?.captured === "number" && (
            <span className="text-[var(--fg-faint)]">{status.captured} requests captured</span>
          )}
        </div>

        {/* body */}
        <div className="flex-1 overflow-hidden bg-black" style={{ minHeight: 540 }}>
          {phase === "done" && capture ? (
            <CapturePanel capture={capture} />
          ) : showIframe ? (
            <iframe
              title="noVNC remote browser"
              src={status!.novnc_url!}
              className="w-full h-full"
              style={{ border: "none", minHeight: 540 }}
              allow="clipboard-read; clipboard-write"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ minHeight: 540 }}>
              <div className="text-center px-6">
                <div className="font-mono text-[11px] text-white/55 uppercase tracking-folio">
                  {phase.replace(/_/g, " ")}
                </div>
                <div className="font-display text-[20px] text-white/85 mt-2">
                  {phase === "starting"     && "Spinning up the remote browser…"}
                  {phase === "x_ready"      && "Display ready, starting VNC…"}
                  {phase === "vnc_ready"    && "VNC ready, starting noVNC…"}
                  {phase === "novnc_ready"  && "Web bridge up, launching Chrome…"}
                  {phase === "browser_ready"&& "Loading the page…"}
                  {phase === "capturing"    && "Capturing the full session…"}
                  {phase === "done"         && "Session captured."}
                  {phase === "failed"       && "Failed — see the message above."}
                  {(phase === "cancelled" || phase === "timeout") && "Session ended."}
                </div>
                {(phase === "failed" || phase === "timeout") && status?.message && (
                  <pre className="mt-3 text-white/45 text-[10px] max-w-[640px] mx-auto whitespace-pre-wrap text-left">
                    {status.message.slice(0, 900)}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>

        {/* "Logged in to the e-paper?" — shown for BOTH login and navigate
            captures (navigate re-opens an existing session, which may also be
            a logged-in one). Records Channel_Crawler.LoggedInCapture (audit
            only; runner ignores it). */}
        {(mode === "login" || mode === "navigate") && (
          <div className="px-5 py-3 flex items-center gap-3 flex-wrap"
               style={{ borderTop: "1px solid var(--rule)",
                        background: didLogin ? "rgba(43,122,74,0.10)" : "var(--bg-inset)" }}>
            <button
              type="button"
              role="switch"
              aria-checked={didLogin}
              onClick={() => { const v = !didLogin; setDidLogin(v); markLoggedIn.mutate(v); }}
              className="relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors"
              style={{ background: didLogin ? "var(--primary)" : "var(--rule-strong)" }}
            >
              <span className="inline-block h-4 w-4 rounded-full bg-white transition-transform mt-0.5"
                    style={{ transform: didLogin ? "translateX(18px)" : "translateX(2px)" }} />
            </button>
            <div className="leading-tight">
              <div className="font-display text-[14px]">
                Logged in to the e-paper?
              </div>
              <div className="font-mono text-[10px] text-[var(--fg-muted)]">
                Tick if you actually signed in (paywalled paper). Leave off for an anonymous capture.
                {markLoggedIn.isPending && <span className="ml-1.5 text-[var(--fg-faint)]">saving…</span>}
              </div>
            </div>
          </div>
        )}

        {/* credentials strip — shown whenever a real login is declared, in
            BOTH login and navigate captures. Empty if none stored; the
            placeholder shows "(on file)" when credentials already exist. */}
        {(mode === "login" || mode === "navigate") && didLogin && (
          <CredentialsStrip channelId={row.channel_id} hasCreds={row.has_credentials} />
        )}

        {err && (
          <div className="px-5 py-2 np-banner">
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
            {err}
          </div>
        )}

        {/* footer */}
        <div className="px-5 py-3 flex items-center justify-between gap-2"
             style={{ borderTop: "1px solid var(--rule)" }}>
          <div className="font-mono text-[10px] text-[var(--fg-faint)]">
            {status?.session_id && `session ${status.session_id}`}
          </div>
          <div className="flex items-center gap-2">
            {terminal ? (
              <button onClick={onClose} className="np-btn">
                <Check size={11} className="inline mr-1" />Close
              </button>
            ) : (
              <>
                <button onClick={() => cancel.mutate()} className="np-btn-ghost"
                        disabled={cancel.isPending}>
                  {cancel.isPending ? "Cancelling…" : "Cancel"}
                </button>
                {phase === "waiting_for_login" && (
                  <button onClick={() => loggedIn.mutate()} className="np-btn"
                          disabled={loggedIn.isPending}>
                    {loggedIn.isPending
                      ? "…"
                      : <><Check size={11} className="inline mr-1" />I'm logged in</>}
                  </button>
                )}
                {phase === "waiting_for_navigate" && (
                  <button onClick={() => done.mutate()} className="np-btn"
                          disabled={done.isPending}>
                    {done.isPending
                      ? "Capturing…"
                      : <><Check size={11} className="inline mr-1" />Finish &amp; capture</>}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ────────────────── Credentials strip ──────────────────────────────
   Operator types the publisher username/password here. Stored plaintext
   on AuthProfile_Crawler via the API — never sent to np92. */
function CredentialsStrip({
  channelId, hasCreds,
}: { channelId: number; hasCreds: boolean }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const save = useMutation({
    mutationFn: () => setCredentials(channelId, { username, password }),
  });

  return (
    <div className="px-5 py-2.5 flex items-center gap-2 flex-wrap"
         style={{ borderTop: "1px solid var(--rule)", background: "var(--bg-inset)" }}>
      <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
        Credentials
      </span>
      <input
        value={username} onChange={(e) => setUsername(e.target.value)}
        placeholder={hasCreds ? "username (on file)" : "publisher username"}
        className="np-input text-[12px] py-1 px-2 w-[200px]"
        autoComplete="off"
      />
      <input
        value={password} onChange={(e) => setPassword(e.target.value)}
        type="password" placeholder={hasCreds ? "password (on file)" : "publisher password"}
        className="np-input text-[12px] py-1 px-2 w-[200px]"
        autoComplete="new-password"
      />
      <button
        onClick={() => save.mutate()}
        disabled={save.isPending || (!username && !password)}
        className="np-btn-ghost text-[11px]"
      >
        {save.isPending ? "Saving…" : save.isSuccess ? "Saved ✓" : "Save credentials"}
      </button>
      <span className="font-mono text-[10px] text-[var(--fg-faint)]">
        stored plaintext · never sent to np92 · for future automated re-login
      </span>
    </div>
  );
}


/* ────────────────── Capture panel ──────────────────────────────────
   Renders the full capture artifact written by playwright_session.py. */
function CapturePanel({ capture }: { capture: unknown }) {
  const c = capture as {
    cookie_count?: number;
    network_request_count?: number;
    network?: any[];
    session_storage?: Record<string, unknown>;
    auth_domain_map?: Record<string, any>;
    cookie_expiry_table?: any[];
    final_url?: string;
  };
  const reqs = c.network ?? [];
  const jsonHits = reqs.filter(r =>
    r.status >= 200 && r.status < 400 &&
    typeof r.content_type === "string" &&
    (r.content_type.includes("json") ||
     (r.content_type.startsWith("text/") && !r.content_type.startsWith("text/css")))
  );
  const authDomains = Object.entries(c.auth_domain_map ?? {});
  const ssOrigins = Object.keys(c.session_storage ?? {});

  return (
    <div className="flex-1 overflow-auto p-4 bg-[var(--bg-card)]" style={{ minHeight: 540 }}>
      {/* summary tiles */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <Tile icon={<Database size={12} />} label="cookies" value={c.cookie_count ?? 0} />
        <Tile icon={<Globe size={12} />} label="requests"
              value={c.network_request_count ?? reqs.length} />
        <Tile icon={<Globe size={12} />} label="auth domains" value={authDomains.length} />
        <Tile icon={<Database size={12} />} label="sessionStorage origins" value={ssOrigins.length} />
      </div>

      {/* per-domain auth map — this is what catches multi-domain auth (BS) */}
      {authDomains.length > 0 && (
        <div className="mb-4">
          <div className="np-label mb-1.5">Per-domain auth map</div>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                <Th>Domain</Th><Th>Requests</Th><Th>Auth headers</Th>
                <Th>Set-Cookie</Th><Th>Bearer?</Th>
              </tr>
            </thead>
            <tbody>
              {authDomains.map(([host, info]) => (
                <tr key={host} style={{ borderTop: "1px solid var(--rule)" }}>
                  <Td><span className="font-mono text-[10px]">{host}</span></Td>
                  <Td><span className="font-mono text-[10px]">{info.request_count}</span></Td>
                  <Td><span className="font-mono text-[10px] text-[var(--fg-muted)]">
                    {(info.auth_headers ?? []).join(", ") || "—"}</span></Td>
                  <Td><span className="font-mono text-[10px] text-[var(--fg-muted)] break-all">
                    {(info.set_cookie_names ?? []).join(", ") || "—"}</span></Td>
                  <Td><span className="font-mono text-[10px]">
                    {info.saw_bearer ? "yes" : "—"}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* JSON / API endpoints */}
      <div className="np-label mb-1.5">
        API endpoints — {jsonHits.length} JSON/text of {reqs.length} captured
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
            <Th>Status</Th><Th>Method</Th><Th>URL</Th><Th>Type</Th><Th>Body snippet</Th>
          </tr>
        </thead>
        <tbody>
          {jsonHits.map((r, i) => (
            <tr key={i} style={{ borderTop: "1px solid var(--rule)" }}>
              <Td><span className="font-mono text-[10px]">{r.status}</span></Td>
              <Td><span className="font-mono text-[10px]">{r.method}</span></Td>
              <Td><div className="font-mono text-[10px] break-all max-w-[420px]">{r.url}</div></Td>
              <Td><span className="font-mono text-[10px] text-[var(--fg-muted)]">
                {(r.content_type || "").slice(0, 28)}</span></Td>
              <Td><div className="font-mono text-[10px] text-[var(--fg-muted)] break-all max-w-[260px] line-clamp-2">
                {(r.body_snippet || "").slice(0, 160)}</div></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tile({ icon, label, value }: {
  icon: React.ReactNode; label: string; value: number;
}) {
  return (
    <div className="np-card-inset px-3 py-2">
      <div className="flex items-center gap-1.5 text-[var(--fg-faint)]">
        {icon}
        <span className="font-mono text-[9px] uppercase tracking-folio">{label}</span>
      </div>
      <div className="font-display text-[22px] mt-0.5">{value}</div>
    </div>
  );
}


/* ────────────────── small bits ────────────────────────────────────── */
function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-left font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] ${className ?? ""}`}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2.5 ${className ?? ""}`}>{children}</td>;
}

function PhasePill({ phase }: { phase: string }) {
  const palette: Record<string, [string, string]> = {
    starting:             ["var(--fg-faint)", "starting"],
    x_ready:              ["var(--fg-faint)", "display up"],
    vnc_ready:            ["var(--fg-faint)", "vnc up"],
    novnc_ready:          ["var(--fg-faint)", "web up"],
    browser_ready:        ["#c47c2e",         "browser loaded"],
    waiting_for_login:    ["var(--primary)",  "log in now"],
    waiting_for_navigate: ["var(--primary)",  "navigating"],
    capturing:            ["#c47c2e",         "capturing"],
    done:                 ["var(--primary)",  "done"],
    failed:               ["var(--accent)",   "failed"],
    cancelled:            ["var(--accent)",   "cancelled"],
    timeout:              ["var(--accent)",   "timed out"],
    idle:                 ["var(--fg-faint)", "idle"],
  };
  const [col, txt] = palette[phase] ?? ["var(--fg-faint)", phase];
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-folio"
          style={{ background: "var(--bg-inset)", color: col }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: col }} />
      {txt}
    </span>
  );
}
